import type { IconProp } from '@fortawesome/fontawesome-svg-core';

import type { Async } from '@client/new/helpers/types';

import {
  AutomationConfigField,
  ConversionNoun,
  SECTION_COPY,
  SECTION_ORDER,
  copyFor,
} from '../automation-copy';
import {
  CreatorAutomationItem,
  UpdateAutomationConfigRequest,
} from '../automations.api';
import {
  AutomationsLoadError,
  AutomationsModel,
  AutomationsUpdateError,
  AutomationsUpdateStatus,
  useAutomationsModel,
} from '../model/Automations.model';
import { useAutomationStatsModel } from '../model/AutomationInsights.model';

const ID_FORMAT_MESSAGE = 'Must be a positive integer id';

/** Text fields the admin edits before an explicit save. Ids stay strings so
 *  a half-typed value is representable; empty clears the key on save. */
export type AutomationConfigDraft = {
  firstWorkoutId: string;
  otpPitchTargetProductId: string;
};

export type AutomationConfigValidation = Partial<
  Record<keyof AutomationConfigDraft, string>
>;

export type AutomationRow = {
  automationType: string;
  displayName: string;
  description: string;
  icon: IconProp;
  /** "4 emails and 3 pushes over 5 days", server-computed. */
  cadence: string;
  /** "Starts when: …", server-computed. */
  triggerLine: string;
  /** "12 recovered carts to date"; absent when the type has no noun, stats are not in yet, or nothing converted. */
  conversionLine?: string;
  needsSetup: boolean;
  /** Creator-facing line for the disabled switch's tooltip; null unless the row needs setup. */
  setupHint: string | null;
  sendsPush: boolean;
  enabled: boolean;
  pending: boolean;
  error?: string;
  retry?: () => Promise<boolean>;
  status?: string;
  disableConfirmation: { title: string; body: string };
};

export type AutomationSection = {
  key: string;
  title: string;
  subtitle: string;
  rows: AutomationRow[];
};

export type AutomationsViewModel = {
  sections: AutomationSection[];
  /** Rows the enable gate would reject today; drives the banner. */
  needsSetup: { count: number; firstAutomationType: string } | null;
  adminTools: boolean;
  retry: AutomationsModel['retry'];
  update: (automationType: string, enabled: boolean) => Promise<boolean>;
};

type AutomationsViewModelError = {
  message: string;
  retry: () => Promise<void>;
};

const LOAD_ERROR_MESSAGES: Record<AutomationsLoadError['kind'], string> = {
  unauthorized:
    'Your session has expired. Sign in again to manage automations.',
  forbidden: "You don't have permission to manage automations.",
  network: "We couldn't load automations.",
  contract: 'No automations are available.',
};

const UPDATE_ERROR_MESSAGES: Record<
  Exclude<AutomationsUpdateError['kind'], 'rejected'>,
  string
> = {
  unauthorized:
    'Your session has expired. Your previous setting has been restored.',
  forbidden:
    "You don't have permission to change this automation. Your previous setting has been restored.",
  network:
    "We couldn't update this automation. Your previous setting has been restored. Try again.",
};

/** Config saves are not optimistic, so nothing is rolled back on failure. */
const CONFIG_ERROR_MESSAGES: Record<
  Exclude<AutomationsUpdateError['kind'], 'rejected'>,
  string
> = {
  unauthorized:
    'Your session has expired. The configuration was not saved. Sign in again.',
  forbidden:
    "You don't have permission to change this automation. The configuration was not saved.",
  network: "We couldn't save this configuration. Try again.",
};

const statusMessage = (
  status: AutomationsUpdateStatus | undefined,
  displayName: string,
): string | undefined => {
  if (!status) return undefined;
  return status.kind === 'saving'
    ? `Saving ${displayName}`
    : `${displayName} saved`;
};

const errorMessage = (failure: AutomationsUpdateError): string => {
  if (failure.kind === 'rejected') {
    return failure.onEnable
      ? "This automation can't be switched on yet. Open it to finish the setup."
      : "That change couldn't be saved. Open the automation and check its details.";
  }
  return failure.intendedEnabled === undefined
    ? CONFIG_ERROR_MESSAGES[failure.kind]
    : UPDATE_ERROR_MESSAGES[failure.kind];
};

const isValidId = (raw: string): boolean =>
  raw === '' || /^[1-9]\d*$/.test(raw);

export const validateConfigDraft = (
  draft: AutomationConfigDraft,
  fields: readonly AutomationConfigField[],
): AutomationConfigValidation => {
  const validation: AutomationConfigValidation = {};
  if (fields.includes('firstWorkout') && !isValidId(draft.firstWorkoutId)) {
    validation.firstWorkoutId = ID_FORMAT_MESSAGE;
  }
  if (
    fields.includes('otpPitchTargetProductId') &&
    !isValidId(draft.otpPitchTargetProductId)
  ) {
    validation.otpPitchTargetProductId = ID_FORMAT_MESSAGE;
  }
  return validation;
};

/** Only changed keys go on the wire: re-sending firstWorkout would make the
 *  server mint a fresh deep link for an unchanged workout. */
export const toConfigRequest = (
  draft: AutomationConfigDraft,
  saved: AutomationConfigDraft,
  fields: readonly AutomationConfigField[],
): UpdateAutomationConfigRequest => {
  const request: UpdateAutomationConfigRequest = {};
  if (
    fields.includes('firstWorkout') &&
    draft.firstWorkoutId !== saved.firstWorkoutId
  ) {
    request.firstWorkout =
      draft.firstWorkoutId === ''
        ? null
        : { kind: 'workout', workoutId: Number(draft.firstWorkoutId) };
  }
  if (
    fields.includes('otpPitchTargetProductId') &&
    draft.otpPitchTargetProductId !== saved.otpPitchTargetProductId
  ) {
    request.otpPitchTargetProductId =
      draft.otpPitchTargetProductId === ''
        ? null
        : draft.otpPitchTargetProductId;
  }
  return request;
};

export const toConversionLine = (
  converted: number | undefined,
  noun: ConversionNoun | undefined,
): string | undefined =>
  noun && converted !== undefined && converted > 0
    ? `${converted} ${converted === 1 ? noun.one : noun.many} to date`
    : undefined;

const humanize = (key: string): string => {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

type RowWithSection = AutomationRow & { section: string };

/** Known sections in portal order, then any section the server invented,
 *  in the order its rows arrived. Empty sections are not rendered. */
export const groupBySection = (rows: RowWithSection[]): AutomationSection[] => {
  const order: string[] = [...SECTION_ORDER];
  for (const row of rows) {
    if (!order.includes(row.section)) order.push(row.section);
  }
  return order.flatMap((key) => {
    const sectionRows = rows.filter((row) => row.section === key);
    if (sectionRows.length === 0) return [];
    const copy =
      key in SECTION_COPY
        ? SECTION_COPY[key as keyof typeof SECTION_COPY]
        : { title: humanize(key), subtitle: '' };
    return [{ key, ...copy, rows: sectionRows }];
  });
};

export const useAutomationsViewModel = (
  creatorId: number,
): Async<AutomationsViewModel, AutomationsViewModelError> => {
  const model = useAutomationsModel(creatorId);
  // Same query the insights drawer uses; the list only reads `converted`.
  // While it loads or fails the rows simply have no conversion line.
  const stats = useAutomationStatsModel(creatorId);
  const statsByType = new Map(
    stats.type === 'fetched'
      ? stats.value.stats.map((entry) => [entry.automationType, entry])
      : [],
  );

  if (model.type === 'fetch-error') {
    return {
      type: 'fetch-error',
      value: {
        message: LOAD_ERROR_MESSAGES[model.value.kind],
        retry: model.value.retry,
      },
    };
  }
  if (model.type !== 'fetched') return model;

  const rows = model.value.automations.map(
    (automation: CreatorAutomationItem): RowWithSection => {
      const { automationType } = automation;
      const failure = model.value.errors[automationType];
      const copy = copyFor(automationType);
      const pending = model.value.pending.has(automationType);
      const intendedEnabled =
        failure && failure.kind !== 'rejected'
          ? failure.intendedEnabled
          : undefined;
      const entry = statsByType.get(automationType);
      const pushesOff = automation.config?.includePushes === false;

      return {
        automationType,
        section: automation.section,
        displayName: automation.displayName,
        description: automation.description,
        icon: copy.icon,
        cadence:
          pushesOff && entry?.cadenceEmailOnly
            ? entry.cadenceEmailOnly
            : automation.cadence,
        triggerLine: automation.triggerLine,
        conversionLine: toConversionLine(entry?.converted, copy.conversionNoun),
        needsSetup: automation.status === 'needs_setup',
        setupHint: automation.status === 'needs_setup' ? copy.setupHint : null,
        sendsPush: automation.channels.includes('push') && !pushesOff,
        enabled: automation.enabled,
        pending,
        error: failure ? errorMessage(failure) : undefined,
        retry:
          intendedEnabled !== undefined
            ? () => model.value.update(automationType, intendedEnabled)
            : undefined,
        status: statusMessage(
          model.value.statuses[automationType],
          automation.displayName,
        ),
        disableConfirmation: {
          title: `Turn off ${automation.displayName}?`,
          body: 'Anyone partway through the sequence stops getting the rest, and that cannot be resumed. Turning it back on only applies to new enrollments.',
        },
      };
    },
  );

  const needing = rows.filter((row) => row.needsSetup);
  return {
    type: 'fetched',
    value: {
      sections: groupBySection(rows),
      needsSetup:
        needing.length > 0
          ? {
              count: needing.length,
              firstAutomationType: needing[0].automationType,
            }
          : null,
      adminTools: model.value.adminTools,
      retry: model.value.retry,
      update: (automationType, enabled) =>
        model.value.update(automationType, enabled),
    },
  };
};
